/**
 * 内容解析服务
 * 从URL提取元数据、封面、标题等信息
 */

import { detectContentType, detectTypeByExtension } from '@/data/contentTypes'

class ContentParser {
  
  // 解析URL获取内容信息
  async parseUrl(url) {
    const result = {
      url,
      title: '',
      description: '',
      cover: '',
      type: 'other',
      platform: null,
      favicon: '',
      author: '',
      publishTime: null,
      tags: [],
      metadata: {}
    }

    try {
      // 1. 先通过URL模式识别类型
      const detected = detectContentType(url)
      result.type = detected.type
      result.platform = detected.platform

      // 2. 检查文件扩展名
      const extType = detectTypeByExtension(url)
      if (extType) {
        result.type = extType
      }

      // 3. 尝试获取页面元数据
      const metadata = await this.fetchMetadata(url)
      if (metadata) {
        result.title = metadata.title || this.extractTitleFromUrl(url)
        result.description = metadata.description || ''
        result.cover = metadata.image || ''
        result.favicon = metadata.favicon || this.getFaviconUrl(url)
        result.author = metadata.author || ''
        result.publishTime = metadata.publishTime || null
        result.metadata = metadata
      } else {
        result.title = this.extractTitleFromUrl(url)
        result.favicon = this.getFaviconUrl(url)
      }

      // 4. 特殊平台处理
      await this.handleSpecialPlatforms(result)

    } catch (error) {
      console.error('解析URL失败:', error)
      result.title = this.extractTitleFromUrl(url)
      result.favicon = this.getFaviconUrl(url)
    }

    return result
  }

  // 获取页面元数据（通过代理服务或CORS）
  async fetchMetadata(url) {
    try {
      // 方案1: 使用免费的元数据API
      const apiUrl = `https://api.microlink.io/?url=${encodeURIComponent(url)}`
      const response = await fetch(apiUrl, { 
        signal: AbortSignal.timeout(5000)
      })
      
      if (response.ok) {
        const data = await response.json()
        if (data.status === 'success' && data.data) {
          return {
            title: data.data.title,
            description: data.data.description,
            image: data.data.image?.url,
            favicon: data.data.logo?.url,
            author: data.data.author,
            publishTime: data.data.date
          }
        }
      }
    } catch (error) {
      console.warn('Microlink API失败，尝试备用方案')
    }

    try {
      // 方案2: 使用Open Graph解析
      const ogApiUrl = `https://opengraph.io/api/1.1/site/${encodeURIComponent(url)}?app_id=demo`
      const response = await fetch(ogApiUrl, {
        signal: AbortSignal.timeout(5000)
      })
      
      if (response.ok) {
        const data = await response.json()
        if (data.hybridGraph) {
          return {
            title: data.hybridGraph.title,
            description: data.hybridGraph.description,
            image: data.hybridGraph.image,
            favicon: data.hybridGraph.favicon,
            author: data.hybridGraph.author
          }
        }
      }
    } catch (error) {
      console.warn('OpenGraph API失败')
    }

    try {
      // 方案3: 通过CORS代理拉取页面HTML，本地解析meta信息
      const proxyUrl = `https://api.allorigins.win/get?url=${encodeURIComponent(url)}`
      const response = await fetch(proxyUrl, {
        signal: AbortSignal.timeout(8000)
      })

      if (response.ok) {
        const data = await response.json()
        if (data.contents) {
          const doc = new DOMParser().parseFromString(data.contents, 'text/html')
          const meta = selector =>
            doc.querySelector(selector)?.getAttribute('content')?.trim() || ''

          const title =
            doc.querySelector('title')?.textContent?.trim() ||
            meta('meta[property="og:title"]')
          const description =
            meta('meta[name="description"]') || meta('meta[property="og:description"]')
          const image = meta('meta[property="og:image"]')

          let favicon =
            doc.querySelector('link[rel~="icon"]')?.getAttribute('href') ||
            doc.querySelector('link[rel~="shortcut"]')?.getAttribute('href') ||
            ''
          if (favicon && !/^https?:/i.test(favicon)) {
            favicon = new URL(favicon, url).href
          }

          if (title || description) {
            return { title, description, image, favicon }
          }
        }
      }
    } catch (error) {
      console.warn('CORS代理解析失败')
    }

    return null
  }

  // 从URL提取标题
  extractTitleFromUrl(url) {
    try {
      const urlObj = new URL(url)
      const pathname = urlObj.pathname
      
      // 移除开头的斜杠和文件扩展名
      let title = pathname
        .replace(/^\//, '')
        .replace(/\.[^/.]+$/, '')
        .replace(/[-_]/g, ' ')
        .replace(/\//g, ' - ')
      
      if (!title) {
        title = urlObj.hostname.replace('www.', '')
      }
      
      return title
    } catch {
      return url
    }
  }

  // 获取网站favicon
  getFaviconUrl(url) {
    try {
      const urlObj = new URL(url)
      return `https://www.google.com/s2/favicons?domain=${urlObj.hostname}&sz=64`
    } catch {
      return ''
    }
  }

  // 处理特殊平台的额外信息
  async handleSpecialPlatforms(result) {
    const { url, platform } = result

    // YouTube: 提取视频ID获取封面
    if (platform === 'YouTube') {
      const videoId = this.extractYouTubeId(url)
      if (videoId) {
        result.cover = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
        result.metadata.videoId = videoId
        result.metadata.embedUrl = `https://www.youtube.com/embed/${videoId}`
      }
    }

    // B站: 提取BV号
    if (platform === 'B站') {
      const bvMatch = url.match(/BV[\w]+/i)
      if (bvMatch) {
        result.metadata.bvid = bvMatch[0]
      }
    }

    // GitHub: 解析仓库信息
    if (platform === 'GitHub') {
      const repoMatch = url.match(/github\.com\/([\w-]+)\/([\w-]+)/i)
      if (repoMatch) {
        result.metadata.owner = repoMatch[1]
        result.metadata.repo = repoMatch[2]
        result.cover = `https://opengraph.githubassets.com/1/${repoMatch[1]}/${repoMatch[2]}`
      }
    }

    // Twitter: 提取推文ID
    if (platform === 'Twitter') {
      const tweetMatch = url.match(/status\/(\d+)/i)
      if (tweetMatch) {
        result.metadata.tweetId = tweetMatch[1]
      }
    }
  }

  // 提取YouTube视频ID
  extractYouTubeId(url) {
    const patterns = [
      /youtube\.com\/watch\?v=([\w-]+)/i,
      /youtu\.be\/([\w-]+)/i,
      /youtube\.com\/embed\/([\w-]+)/i
    ]
    
    for (const pattern of patterns) {
      const match = url.match(pattern)
      if (match) return match[1]
    }
    return null
  }

  // 解析剪贴板内容
  async parseClipboard() {
    try {
      const text = await navigator.clipboard.readText()
      
      // 检查是否是URL
      if (this.isValidUrl(text)) {
        return this.parseUrl(text.trim())
      }

      // 检查是否包含URL（从分享文本中提取）
      const urlMatch = text.match(/https?:\/\/[^\s]+/i)
      if (urlMatch) {
        const result = await this.parseUrl(urlMatch[0])
        // 保留分享文本中的其他内容作为备注
        const note = text.replace(urlMatch[0], '').trim()
        if (note) {
          result.note = note
        }
        return result
      }

      // 纯文本内容
      return {
        type: 'note',
        title: text.slice(0, 50) + (text.length > 50 ? '...' : ''),
        description: text,
        url: ''
      }
    } catch (error) {
      console.error('读取剪贴板失败:', error)
      throw error
    }
  }

  // 验证URL格式
  isValidUrl(str) {
    try {
      new URL(str)
      return true
    } catch {
      return false
    }
  }

  // 生成智能标签
  generateTags(content) {
    const tags = []
    
    // 根据平台添加标签
    if (content.platform) {
      tags.push(content.platform)
    }

    // 根据类型添加标签
    const typeLabels = {
      video: '视频',
      article: '文章',
      image: '图片',
      code: '代码',
      audio: '音频'
    }
    if (typeLabels[content.type]) {
      tags.push(typeLabels[content.type])
    }

    // 从标题和描述提取关键词
    const text = `${content.title} ${content.description}`.toLowerCase()
    const techKeywords = {
      'vue': 'Vue',
      'react': 'React',
      'javascript': 'JavaScript',
      'typescript': 'TypeScript',
      'python': 'Python',
      'flutter': 'Flutter',
      'node': 'Node.js',
      'css': 'CSS',
      'ai': 'AI',
      'gpt': 'GPT',
      'machine learning': '机器学习',
      'tutorial': '教程',
      '教程': '教程',
      '入门': '入门',
      '实战': '实战'
    }

    for (const [keyword, tag] of Object.entries(techKeywords)) {
      if (text.includes(keyword) && !tags.includes(tag)) {
        tags.push(tag)
      }
    }

    return tags.slice(0, 5) // 最多5个标签
  }
}

export const contentParser = new ContentParser()
export default contentParser
